import React, { useState, useRef, useMemo } from 'react'

import Box from '@mui/material/Box'
import { useIntl } from 'react-intl'
import AddIcon from '@mui/icons-material/Add'
import { LoanRole } from '@matech/thebigpos-sdk'
import Typography from '@mui/material/Typography'
import CircularProgress from '@mui/material/CircularProgress'
import type { AutocompleteInputChangeReason } from '@mui/material/Autocomplete'

import useUser from '../../../../../hooks/useUser'
import { LabelValuePair } from '../../../../../types'
import Select, {
	type SelectChangeEvent,
} from '../../../../../components/formControls/Select'
import { Roles } from '../../../../../services/client'
import { Button } from '../../../../../components/Button'
import Dialog from '../../../../../components/modals/Dialog'
import UsersSelect from '../../../../../components/UsersSelect'
import ModalUserGroupsAccessList from './ModalUserGroupsAccessList'
import BranchesSelect from '../../../../../components/BranchesSelect'
import FormControl from '../../../../../components/formControls/FormControl'
// Task Name: [WEB] - Fix circular dependence imports in Share Loan feature
// Task ID: 9880243555
// Task Link: https://mortgage-automation-technologies.monday.com/boards/5842126081/pulses/9880243555
import { LoanUserRow } from '../../../../../components/loanAppDetails/LoanUsersTable'

const USERS_FIELD_FILTER_ROLES = [
	Roles.loanOfficer,
	Roles.loanProcessor,
]

type AccessItemType = 'Branch' | 'User'

const ACCESS_ITEM_TYPES = {
	BRANCH: 'Branch' as const,
	USER: 'User' as const,
} satisfies Record<string, AccessItemType>

export type UserGroupScopeItem = {
	id: string
	value: string
	label: string
	type: AccessItemType
	role?: LoanRole
}

export type ModalUserGroupsAccessProps = {
	visible: boolean
	onClose: () => void
	onSubmit?: (data: UserGroupScopeItem[]) => Promise<void>
	isSubmitting?: boolean
	isLoading?: boolean
	isLoadingAdding?: boolean
	initialAccessItems?: UserGroupScopeItem[]
	showActions?: boolean
}

type ValidateUserError = {
	userMessageKey?: string
	roleMessageKey?: string
	duplicateMessageKey?: string
}

const ModalUserGroupsAccess = ({
	visible,
	onClose,
	onSubmit,
	isSubmitting,
	initialAccessItems,
	showActions = true,
	isLoading,
	isLoadingAdding,
}: ModalUserGroupsAccessProps) => {
	const { user } = useUser()
	const { formatMessage } = useIntl()
	const autocompleteRef = useRef<HTMLDivElement>(null)

	const [inputValue, setInputValue] = useState('')
	const [branchInputValue, setBranchInputValue] = useState('')

	const [validationErrors, setValidationErrors] =
		useState<ValidateUserError>({})
	const [selectedLocalUser, setSelectedLocalUser] =
		useState<LoanUserRow | null>(null)
	const [selectedLocalType, setSelectedLocalType] =
		useState<AccessItemType>(ACCESS_ITEM_TYPES.USER)
	const [selectedLocalBranch, setSelectedLocalBranch] =
		useState<LabelValuePair | null>(null)
	const [accessItemsList, setAccessItemsList] = useState<
		UserGroupScopeItem[]
	>([])

	const disableTableButtons = isLoadingAdding
	const disableAddUserButton =
		(selectedLocalType === ACCESS_ITEM_TYPES.USER &&
			!selectedLocalUser) ||
		(selectedLocalType === ACCESS_ITEM_TYPES.BRANCH &&
			!selectedLocalBranch) ||
		isLoadingAdding ||
		isSubmitting

	const modalTitle = formatMessage({
		id: 'user_groups.title_modal_group_access',
	})

	const typeMenuItems = useMemo(
		() => [
			{
				value: ACCESS_ITEM_TYPES.BRANCH,
				label: formatMessage({
					id: 'entity_type.branch',
				}),
			},
			{
				value: ACCESS_ITEM_TYPES.USER,
				label: formatMessage({
					id: 'entity_type.user',
				}),
			},
		],
		[formatMessage]
	)

	const handleTypeChange = (e: SelectChangeEvent<unknown>) => {
		setSelectedLocalType(e.target.value as AccessItemType)
		setValidationErrors({})
		setSelectedLocalUser(null)
		setSelectedLocalBranch(null)
		setInputValue('')
		setBranchInputValue('')

		if (validationErrors.roleMessageKey) {
			setValidationErrors((prev) => ({
				...prev,
				role: undefined,
			}))
		}
	}

	const selectUserError =
		(selectedLocalType === ACCESS_ITEM_TYPES.USER &&
			!!validationErrors.userMessageKey) ||
		(selectedLocalType === ACCESS_ITEM_TYPES.USER &&
			!!validationErrors.duplicateMessageKey)

	const selectBranchError =
		(selectedLocalType === ACCESS_ITEM_TYPES.BRANCH &&
			!!validationErrors.userMessageKey) ||
		(selectedLocalType === ACCESS_ITEM_TYPES.BRANCH &&
			!!validationErrors.duplicateMessageKey)

	const helperTextKey =
		validationErrors.userMessageKey ??
		validationErrors.duplicateMessageKey ??
		'share_loan.search_and_select_user'

	const branchHelperTextKey =
		validationErrors.userMessageKey ??
		validationErrors.duplicateMessageKey

	const branchHelperText = branchHelperTextKey
		? formatMessage({
				id: branchHelperTextKey,
			})
		: formatMessage({
				id: 'global.search_and_select_branch',
			})

	const handleInputChange = (
		value: string,
		reason: AutocompleteInputChangeReason
	) => {
		if (reason === 'clear') {
			setSelectedLocalUser(null)
			setInputValue('')
			return
		}

		setInputValue(value)
	}

	const handleBranchInputChange = (
		value: string,
		reason: AutocompleteInputChangeReason
	) => {
		if (reason === 'clear') {
			setSelectedLocalBranch(null)
			setBranchInputValue('')
			return
		}

		setBranchInputValue(value)
	}

	const handleAutocompleteClose = () => {
		if (!selectedLocalUser && inputValue) setInputValue('')
	}

	const handleBranchAutocompleteClose = () => {
		if (!selectedLocalBranch && branchInputValue)
			setBranchInputValue('')
	}

	const handleDialogEntered = () => {
		if (autocompleteRef.current) autocompleteRef.current.focus()

		if (initialAccessItems) setAccessItemsList(initialAccessItems)
	}

	const handleDialogExit = () => {
		setSelectedLocalUser(null)
		setSelectedLocalType(ACCESS_ITEM_TYPES.USER)
		setSelectedLocalBranch(null)
		setInputValue('')
		setBranchInputValue('')
		setAccessItemsList([])
		setValidationErrors({})
	}

	const handleChangeUser = (value: LoanUserRow | null) => {
		setSelectedLocalUser(value)

		if (
			validationErrors.userMessageKey ||
			validationErrors.duplicateMessageKey
		)
			setValidationErrors((prev) => ({
				...prev,
				userMessageKey: undefined,
				duplicateMessageKey: undefined,
			}))

		if (value && selectedLocalType === ACCESS_ITEM_TYPES.USER) {
			const isDuplicate = accessItemsList.some(
				(item) =>
					item.type === ACCESS_ITEM_TYPES.USER &&
					item.value === value.id
			)

			if (isDuplicate)
				setValidationErrors((prev) => ({
					...prev,
					duplicateMessageKey:
						'share_loan.validation.user_already_added',
				}))
		}
	}

	const handleChangeBranch = (value: LabelValuePair | null) => {
		setSelectedLocalBranch(value)

		if (
			validationErrors.userMessageKey ||
			validationErrors.duplicateMessageKey
		)
			setValidationErrors((prev) => ({
				...prev,
				userMessageKey: undefined,
				duplicateMessageKey: undefined,
			}))

		if (value && selectedLocalType === ACCESS_ITEM_TYPES.BRANCH) {
			const isDuplicate = accessItemsList.some(
				(item) =>
					item.type === ACCESS_ITEM_TYPES.BRANCH &&
					item.value === value.value
			)

			if (isDuplicate)
				setValidationErrors((prev) => ({
					...prev,
					duplicateMessageKey:
						'share_loan.validation.branch_already_added',
				}))
		}
	}

	const validateUserInput = () => {
		const errors: ValidateUserError = {}

		if (
			selectedLocalType === ACCESS_ITEM_TYPES.USER &&
			!selectedLocalUser
		) {
			errors.userMessageKey =
				'share_loan.validation.please_select_user'
		}

		if (
			selectedLocalType === ACCESS_ITEM_TYPES.BRANCH &&
			!selectedLocalBranch
		) {
			errors.userMessageKey =
				'share_loan.validation.please_select_branch'
		}

		if (selectedLocalUser?.id === user.id) {
			errors.userMessageKey =
				'share_loan.validation.cannot_select_self'
		}

		if (
			selectedLocalType === ACCESS_ITEM_TYPES.USER &&
			selectedLocalUser
		) {
			const isDuplicate = accessItemsList.some(
				(item) =>
					item.type === ACCESS_ITEM_TYPES.USER &&
					item.value === selectedLocalUser.id
			)
			if (isDuplicate) {
				errors.duplicateMessageKey =
					'share_loan.validation.user_already_added'
			}
		}

		if (
			selectedLocalType === ACCESS_ITEM_TYPES.BRANCH &&
			selectedLocalBranch
		) {
			const isDuplicate = accessItemsList.some(
				(item) =>
					item.type === ACCESS_ITEM_TYPES.BRANCH &&
					item.value === selectedLocalBranch.value
			)

			if (isDuplicate) {
				errors.duplicateMessageKey =
					'share_loan.validation.branch_already_added'
			}
		}

		setValidationErrors(errors)

		return Object.keys(errors).length > 0
	}

	const handleAddUser = async () => {
		const hasErrors = validateUserInput()

		if (hasErrors) return

		if (
			selectedLocalType === ACCESS_ITEM_TYPES.USER &&
			!selectedLocalUser
		)
			return

		if (
			selectedLocalType === ACCESS_ITEM_TYPES.BRANCH &&
			!selectedLocalBranch
		)
			return

		let newAccessItem: UserGroupScopeItem
		const id = crypto.randomUUID()

		if (selectedLocalType === ACCESS_ITEM_TYPES.USER) {
			newAccessItem = {
				id,
				value: selectedLocalUser!.id,
				label: selectedLocalUser!.name,
				role: selectedLocalUser?.role,
				type: ACCESS_ITEM_TYPES.USER,
			}
		} else {
			newAccessItem = {
				id,
				value: String(selectedLocalBranch!.value),
				label: selectedLocalBranch!.label,
				type: ACCESS_ITEM_TYPES.BRANCH,
			}
		}

		setAccessItemsList((prev) => [...prev, newAccessItem])

		setSelectedLocalUser(null)
		setSelectedLocalBranch(null)
		setInputValue('')
		setBranchInputValue('')
	}

	const handleDeleteAccessItem = (itemId: string) => {
		setAccessItemsList((prev) =>
			prev.filter((item) => item.id !== itemId)
		)

		if (validationErrors.duplicateMessageKey) {
			setValidationErrors((prev) => ({
				...prev,
				duplicateMessageKey: undefined,
			}))
		}
	}

	const handleClose = (
		_event?: object,
		reason?: 'backdropClick' | 'escapeKeyDown'
	) => {
		if (isLoadingAdding) return

		if (reason === 'backdropClick') return

		onClose()
	}

	const validateForm = () => {
		const errors: ValidateUserError = {}

		setValidationErrors(errors)

		return Object.keys(errors).length === 0
	}

	const handleSubmit = async () => {
		if (!validateForm()) return

		await onSubmit?.(accessItemsList)

		handleClose()
	}

	return (
		<Dialog
			aria-labelledby="modal-user-groups-access"
			title={modalTitle}
			open={visible}
			maxWidth="md"
			fullWidth
			onClose={handleClose}
			TransitionProps={{
				onEntered: handleDialogEntered,
				onExit: handleDialogExit,
			}}
			showActions={showActions}
			closeButtonProps={{
				text: formatMessage({
					id: 'global.cancel',
				}),
				onClick: handleClose,
				disabled: disableTableButtons || isSubmitting,
			}}
			submitButtonProps={{
				text: formatMessage({
					id: 'global.save',
				}),
				onClick: handleSubmit,
				loading: isSubmitting,
				disabled: isSubmitting,
			}}
		>
			<Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
				<Box sx={{ flex: 1 }}>
					<FormControl
						variant="standard"
						fullWidth
						required
						error={!!validationErrors.roleMessageKey}
					>
						<Select
							label={formatMessage({
								id: 'global.type',
							})}
							disabled={isSubmitting}
							menuItems={typeMenuItems}
							controlled={false}
							selectProps={{
								value: selectedLocalType,
								onChange: handleTypeChange,
							}}
						/>

						{validationErrors.roleMessageKey && (
							<Typography variant="caption" color="error">
								{formatMessage({
									id: validationErrors.roleMessageKey,
								})}
							</Typography>
						)}
					</FormControl>
				</Box>

				{selectedLocalType === ACCESS_ITEM_TYPES.USER && (
					<Box sx={{ flex: 2 }}>
						<UsersSelect
							value={selectedLocalUser}
							textFieldRef={autocompleteRef}
							onOptionChange={handleChangeUser}
							onInputChange={handleInputChange}
							onOptionClose={handleAutocompleteClose}
							disabled={disableTableButtons}
							filterRoles={USERS_FIELD_FILTER_ROLES}
							error={selectUserError}
							helperText={formatMessage({
								id: helperTextKey,
							})}
						/>
					</Box>
				)}

				{selectedLocalType === ACCESS_ITEM_TYPES.BRANCH && (
					<Box sx={{ flex: 2 }}>
						<BranchesSelect
							value={selectedLocalBranch}
							onOptionChange={handleChangeBranch}
							onInputChange={handleBranchInputChange}
							onOptionClose={handleBranchAutocompleteClose}
							disabled={disableTableButtons}
							error={selectBranchError}
							helperText={branchHelperText}
						/>
					</Box>
				)}

				<Box sx={{ display: 'flex', alignItems: 'center' }}>
					<Button
						text={formatMessage({
							id: 'global.add',
						})}
						onClick={handleAddUser}
						disabled={disableAddUserButton}
						startIcon={
							isLoadingAdding ? (
								<CircularProgress size={20} />
							) : (
								<AddIcon />
							)
						}
					/>
				</Box>
			</Box>

			<ModalUserGroupsAccessList
				loading={isLoading}
				accessItems={accessItemsList}
				onDeleteItem={handleDeleteAccessItem}
			/>
		</Dialog>
	)
}

export default ModalUserGroupsAccess
